import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'

import { Observable } from 'rxjs'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { SettingsApiService } from 'src/app/builder-services/api/settings.api.service'
import { ShippingApiService } from 'src/app/builder-services/api/shipping.api.service'
import { HelpersService } from 'src/app/helpers.service'
import { DomainService } from 'src/app/domain/services/domain.service'

@Injectable({
  providedIn: 'root'
})
export class AppSettingsService {
  private builderUrl: string
  serviceAccFile!: File
  jsonFile!: File
  plistFile!: File
  serviceAccStr = ''
  plistFileStr = ''
  jsonFileStr = ''

  options = {
    headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
  }
  token: any

  constructor(
    private http: HttpClient,
    private helper: HelpersService,
    private builderService: BuilderService,
    private settingsApi: SettingsApiService,
    private shippingApi: ShippingApiService,
    private domainService: DomainService,
  ) {
    this.builderUrl = this.helper.getBuilderUrl()
    this.token = this.builderService.getToken()
  }

  // ── Supabase helpers ──────────────────────────────────────────────────────

  /**
   * Upserts all key-value pairs of a settings object into project_settings.
   * Object/array values are JSON-serialised; primitives are stored as strings.
   */
  private setSupabaseSettings(projectId: number, settingsType: string, data: Record<string, any>): Observable<void> {
    // Serialize to strings first so the creation layer stores identical values to the old direct upsert.
    const fields: Record<string, string> = {}
    for (const [key, value] of Object.entries(data)) {
      fields[key] = value !== null && typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')
    }

    return new Observable((observer) => {
      this.settingsApi.upsertSettingsByType(String(projectId), settingsType, fields)
        .then(() => {
          observer.next()
          observer.complete()
        })
        .catch((error) => observer.error(error))
    })
  }

  /**
   * Raw settings object for a type, values JSON-parsed by the backend.
   * Promise-returning — the shape the screens were already consuming.
   */
  getSettingsByType = (projectId: any, settingsType: string): Promise<Record<string, any>> => {
    return this.settingsApi.getSettingsByType(String(projectId), settingsType)
  }

  /** Upserts key/value settings rows for a settings type. */
  upsertSettingsByType = (projectId: any, settingsType: string, fields: Record<string, any>): Promise<void> => {
    return this.settingsApi.upsertSettingsByType(String(projectId), settingsType, fields)
  }

  /**
   * Fetches all rows for a settings type and reconstructs the settings object.
   * Values that look like JSON are parsed automatically.
   */
  private getSupabaseSettings(projectId: number, settingsType: string): Observable<Record<string, any>> {
    // Backend getSettingsByType applies the identical per-key JSON.parse and
    // returns {} on failure (retrieval layer swallows errors like all reads).
    return new Observable((observer) => {
      this.settingsApi.getSettingsByType(String(projectId), settingsType)
        .then((result) => {
          observer.next(result)
          observer.complete()
        })
        .catch((error) => observer.error(error))
    })
  }

  // ─────────────────────────────────────────────────────────────────────────
  /**
   * Uploads an image to the backend server along with metadata.
   * The backend handles the image storage and processing.
   *
   * @param image The image file to be uploaded.
   * @param id The unique project identifier for associating the image.
   * @param type The type of the image (e.g., "thumbnail", "banner").
   * @returns An Observable that emits the HTTP response from the backend API.
   */

  public uploadImage(image: File, id: any, type: string): Observable<any> {
    //     console.log(image.name, id, type);
    //     // Initialize Firebase
    // //     // Create a root reference
    //     const storage = getStorage();
    // //     // Create a reference to 'mountains.jpg'
    //     const mountainsRef = refStorage(storage, '/project_assets/images/'+id+'/'+image.name);
    //     // 'file' comes from the Blob or File API
    //     uploadBytes(mountainsRef, image).then((snapshot) => {
    //       const formData = new FormData();
    //       this.token = this.builderService.getToken();
    //       formData.append('image', image);
    //       formData.append('token', this.token);
    //       formData.append('projectId', id);
    //       formData.append('image_type', type);

    //       return this.http.post(this.builderUrl + 'receive-image', formData);
    //     });

    // // // Create a reference to 'images/mountains.jpg'
    // const mountainImagesRef = refStorage(storage, 'images/mountains.jpg');

    if (image && image.type && image.type.startsWith('image/')) {
      const maxSize = image.type === 'image/gif' ? 5 * 1024 * 1024 : 2 * 1024 * 1024;
      if (image.size > maxSize) {
        const maxSizeMB = image.type === 'image/gif' ? 5 : 2;
        alert(`File size exceeds the limit of ${maxSizeMB}MB`);
        this.builderService.showUpdateProgress = false;
        this.builderService.showSettingsProgress = false;
        return new Observable<any>((observer) => { observer.complete(); });
      }
    }

    const formData = new FormData()
    this.token = this.builderService.getToken()
    formData.append('image', image)
    formData.append('token', this.token)
    formData.append('projectId', id)
    formData.append('image_type', type)

    return this.http.post(this.builderUrl + 'receive-image', formData)
  }

  /**
   * Uploads a file to the backend server along with metadata.
   * The backend handles the file storage and processing.
   *
   * @param file The file to be uploaded.
   * @param id The unique project identifier for associating the file.
   * @param type The type of the file (e.g., "document", "image").
   * @param name The name of the file (optional, defaults to an empty string).
   * @returns An Observable that emits the HTTP response from the backend API.
   */

  public uploadFile(file: File, id: any, type: string, name: string = ''): Observable<any> {
    const formData = new FormData()
    this.token = this.builderService.getToken()
    formData.append('file', file)
    formData.append('token', this.token)
    formData.append('projectId', id)
    formData.append('file_type', type)
    formData.append('file_name', name)

    return this.http.post(this.builderUrl + 'receive-file', formData)
  }

  /**
   * Receives the encryption key from google play console and sends to the brain
   * the brain will then process the pepk java tool and send back the release key
   * @param id the project id
   * @returns
   */
  public generateReleaseKeys(id: any): Observable<any> {
    const formData = new FormData()
    this.token = this.builderService.getToken()
    formData.append('token', this.token)
    formData.append('projectId', id)

    return this.http.post(this.builderUrl + 'generate-release-keys', formData)
  }

  /**
   * Updates the general settings of a project in Firebase and synchronizes them with the WordPress backend.
   *
   * This function performs the following steps:
   * 1. Updates the general settings in the Firebase database.
   * 2. Fetches existing general settings from the WordPress backend.
   * 3. Merges the new general settings with the existing ones from WordPress.
   * 4. Sends the updated settings back to the WordPress backend for synchronization.
   *
   * @param id The unique project identifier.
   * @param generalSettings The general settings object to be updated and synchronized.
   * @returns An Observable that emits the response after the settings are successfully updated, or an error if the process fails.
   */
  setGeneralSettings = (id: any, generalSettings: any) => {
    generalSettings = JSON.parse(JSON.stringify(generalSettings))
    return new Observable<any>((observer) => {
      // Firebase: this.db.setDatabase('projects/' + id + '/settings/general_settings', generalSettings)
      this.setSupabaseSettings(Number(id), 'general_settings', generalSettings).subscribe(
        (response) => {
          // Save the General Settings to the WP Database, in order for the splash and icon to be updated
          // @TODO make the splash and icon update to be done from the firebase database
          let getData = new URLSearchParams()
          this.token = this.builderService.getToken()
          getData.set('process', 'sync_ui')
          getData.set('projectId', id)
          getData.set('token', this.token)
          let newGeneralSettings = ''
          this.http.post(this.builderUrl + 'get-general-settings', getData.toString(), this.options).subscribe((responseGeneralSettings: any) => {
            if (!responseGeneralSettings) {
              // In multivendor projects this may return false (super vendor email ≠ vendor email, backend token mismatch).
              // That’s fine — we don’t need to sync with backend in this case. Data is already saved in Firebase and works.
              observer.error(responseGeneralSettings);
              return
            }
            for (const key in generalSettings) {
              if (Object.prototype.hasOwnProperty.call(generalSettings, key)) {
                const element = generalSettings[key]
                responseGeneralSettings[key] = element
              }
            }
            newGeneralSettings = responseGeneralSettings
            let postData = new URLSearchParams()
            this.token = this.builderService.getToken()
            postData.set('process', 'sync_ui')
            postData.set('projectId', id)
            postData.set('generalSettings', JSON.stringify(newGeneralSettings))
            postData.set('token', this.token)

            this.http.post(this.builderUrl + 'update-general-settings', postData.toString(), this.options).subscribe(
              (response) => {
                observer.next(response) // Notify the observer that the operation is complete
                observer.complete() // Complete the Observable
              },
              (error) => {
                observer.error(error) // Notify the observer of an error
              }
            )
          }, (error) => {
            observer.error(error) // Notify the observer of an error
          })
        },
        (error) => {
          observer.error(error) // Notify the observer of an error
        }
      )
    })
  }

  /**
   * Retrieves the general settings of a project from Firebase or WordPress backend.
   *
   * This function performs the following:
   * 1. If the builder is in standalone mode, fetches the general settings directly from Firebase.
   * 2. If not in standalone mode, retrieves the general settings from the WordPress backend via an HTTP POST request.
   *
   * @param id The unique project identifier.
   * @returns An Observable that emits the general settings object or an empty object if no data is available.
   */

  getGeneralSettings = (id: any) => {
    if (this.builderService.standalone) {
      return new Observable<any>((observer) => {
        // Firebase: this.db.getDatabase('projects/' + id + '/settings/general_settings')
        this.getSupabaseSettings(Number(id), 'general_settings').subscribe(
          (response) => {
            observer.next(response)
            observer.complete()
          },
          (error) => {
            observer.error(error)
          }
        )
      })
    } else {
      let postData = new URLSearchParams()
      this.token = this.builderService.getToken()
      postData.set('process', 'sync_ui')
      postData.set('projectId', id)
      postData.set('token', this.token)

      return this.http.post(this.builderUrl + 'get-general-settings', postData.toString(), this.options)
    }
  }

  /**
   * Updates the Firebase-specific settings of a project in Firebase and synchronizes them with the WordPress backend.
   *
   * This function performs the following steps:
   * 1. Saves the Firebase-specific settings to the Firebase database.
   * 2. Fetches the current general settings from the WordPress backend.
   * 3. Merges the updated Firebase settings into the existing general settings.
   * 4. Sends the merged settings back to the WordPress backend for synchronization.
   *
   * @param id The unique project identifier.
   * @param firebaseSettings The Firebase-specific settings to be updated and synchronized.
   * @returns An Observable that emits the response after the settings are successfully updated, or an error if the process fails.
   */
  setFirebaseSettings = (id: any, firebaseSettings: any) => {
    firebaseSettings = JSON.parse(JSON.stringify(firebaseSettings))
    return new Observable<any>((observer) => {
      // firebase_settings is its own settings_type — each key stored as a separate row
      this.setSupabaseSettings(Number(id), 'firebase_settings', firebaseSettings).subscribe(
        (response) => {
          // Save the General Settings to the WP Database, in order for the splash and icon to be updated
          // @TODO make the splash and icon update to be done from the firebase database
          let getData = new URLSearchParams()
          this.token = this.builderService.getToken()
          getData.set('process', 'sync_ui')
          getData.set('projectId', id)
          getData.set('token', this.token)
          let newGeneralSettings = ''
          this.http.post(this.builderUrl + 'get-general-settings', getData.toString(), this.options).subscribe(
            (responseGeneralSettings: any) => {
              // Merge firebaseSettings into responseGeneralSettings
              const newGeneralSettings = {
                ...responseGeneralSettings,
                firebase_settings: firebaseSettings
              };
              let postData = new URLSearchParams();
              this.token = this.builderService.getToken();
              postData.set('process', 'sync_ui');
              postData.set('projectId', id);
              postData.set('generalSettings', JSON.stringify(newGeneralSettings));
              postData.set('token', this.token);

              this.http.post(this.builderUrl + 'update-general-settings', postData.toString(), this.options).subscribe(
                (response) => {
                  observer.next(response) // Notify the observer that the operation is complete
                  observer.complete() // Complete the Observable
                },
                (error) => {
                  observer.error(error) // Notify the observer of an error
                }
              )
            },
            (error) => {
              observer.error(error) // Notify the observer of an error
            }
          )
        },
        (error) => {
          observer.error(error) // Notify the observer of an error
        }
      )
    })
  }

  /**
   * Retrieves the Firebase-specific settings for a given project ID from the Firebase database.
   *
   * This function performs the following steps:
   * 1. Fetches the Firebase-specific settings from the Firebase database for the given project ID.
   * 2. If the settings exist, emits them through the Observable.
   * 3. If no settings are found, emits an empty object (`{}`).
   * 4. Handles errors and propagates them to the caller.
   *
   * @param id The unique project identifier.
   * @returns An Observable that emits the Firebase settings object or an empty object if no data is available.
   */
  getFirebaseSettings = (id: any) => {
    return new Observable<any>((observer) => {
      this.getSupabaseSettings(Number(id), 'firebase_settings').subscribe(
        (response) => {
          observer.next(response)
          observer.complete()
        },
        (error) => {
          observer.error(error)
        }
      )
    })
  }

  getMetaPixelSettings = (id: any) => {
    return new Observable<any>((observer) => {
      this.getSupabaseSettings(Number(id), 'meta_pixel_settings').subscribe(
        (response) => {
          observer.next(response)
          observer.complete()
        },
        (error) => {
          observer.error(error)
        }
      )
    })
  }

  setMetaPixelSettings = (id: any, metaPixelSettings: Record<string, any>) => {
    return new Observable<any>((observer) => {
      this.setSupabaseSettings(Number(id), 'meta_pixel_settings', metaPixelSettings).subscribe(
        (response) => {
          observer.next(response)
          observer.complete()
          // Persist meta pixel fields into the dedicated WordPress option the build reads from
          let metaData = new URLSearchParams()
          metaData.set('project_id', id)
          metaData.set('meta_pixel_id', metaPixelSettings['meta_pixel_id'] ?? '')
          metaData.set('meta_client_token', metaPixelSettings['meta_client_token'] ?? '')
          metaData.set('facebook_displayName', metaPixelSettings['facebook_displayName'] ?? '')
          this.http.post(this.builderUrl + 'save_meta_data', metaData.toString(), this.options).subscribe()
          // Sync meta pixel fields into WordPress general_settings
          let getData = new URLSearchParams()
          this.token = this.builderService.getToken()
          getData.set('process', 'sync_ui')
          getData.set('projectId', id)
          getData.set('token', this.token)
          this.http.post(this.builderUrl + 'get-general-settings', getData.toString(), this.options).subscribe((wpSettings: any) => {
            if (!wpSettings) return
            for (const key in metaPixelSettings) {
              if (Object.prototype.hasOwnProperty.call(metaPixelSettings, key)) {
                wpSettings[key] = metaPixelSettings[key]
              }
            }
            let postData = new URLSearchParams()
            this.token = this.builderService.getToken()
            postData.set('process', 'sync_ui')
            postData.set('projectId', id)
            postData.set('generalSettings', JSON.stringify(wpSettings))
            postData.set('token', this.token)
            this.http.post(this.builderUrl + 'update-general-settings', postData.toString(), this.options).subscribe()
          })
        },
        (error) => {
          observer.error(error)
        }
      )
    })
  }

  getHotjarSettings = (id: any) => {
    return new Observable<any>((observer) => {
      this.getSupabaseSettings(Number(id), 'hotjar_settings').subscribe(
        (response) => {
          observer.next(response)
          observer.complete()
        },
        (error) => {
          observer.error(error)
        }
      )
    })
  }

  setHotjarSettings = (id: any, hotjarSettings: Record<string, any>) => {
    return new Observable<any>((observer) => {
      this.setSupabaseSettings(Number(id), 'hotjar_settings', hotjarSettings).subscribe(
        (response) => {
          observer.next(response)
          observer.complete()
        },
        (error) => {
          observer.error(error)
        }
      )
    })
  }

  getGoogleAnalyticsSettings = (id: any) => {
    return new Observable<any>((observer) => {
      this.getSupabaseSettings(Number(id), 'google_analytics_settings').subscribe(
        (response) => {
          observer.next(response)
          observer.complete()
        },
        (error) => {
          observer.error(error)
        }
      )
    })
  }

  setGoogleAnalyticsSettings = (id: any, googleAnalyticsSettings: Record<string, any>) => {
    return new Observable<any>((observer) => {
      this.setSupabaseSettings(Number(id), 'google_analytics_settings', googleAnalyticsSettings).subscribe(
        (response) => {
          observer.next(response)
          observer.complete()
        },
        (error) => {
          observer.error(error)
        }
      )
    })
  }

  /**
   * Updates the application settings of a project in Firebase and synchronizes them with the WordPress backend.
   *
   * This function performs the following steps:
   * 1. Saves the application settings to the Firebase database under the specified project ID.
   * 2. Prepares and sends the updated application settings to the WordPress backend for synchronization.
   * 3. Emits a response upon successful completion or propagates an error if the process fails.
   *
   * @param id The unique project identifier.
   * @param applicationSettings The application settings object to be updated and synchronized.
   * @returns An Observable that emits the response after the settings are successfully updated, or an error if the process fails.
   */

  setApplicationSettings = (id: any, applicationSettings: Object) => {
    applicationSettings = JSON.parse(JSON.stringify(applicationSettings))
    return new Observable<any>((observer) => {
      if ((this.jsonFile || this.plistFile) && this.serviceAccFile) {
        // this.convertFilesToBase64(this.serviceAccFile, this.jsonFile, this.plistFile);
        this.convertFileToBase64(this.serviceAccFile).then((base64String) => {
          ;(applicationSettings as any)['serviceAccBase64'] = base64String

          if (this.jsonFile) {
            this.convertFileToBase64(this.jsonFile).then((base64Str) => {
              ;(applicationSettings as any)['jsonFileBase64'] = base64Str
              this.setDatabase(id, applicationSettings)
            })
          }
          if (this.plistFile) {
            this.convertFileToBase64(this.plistFile).then((base64Str) => {
              ;(applicationSettings as any)['plistFileBase64'] = base64Str
              this.setDatabase(id, applicationSettings)
            })
          }
        })
      } else {
        this.setDatabase(id, applicationSettings)
      }
      observer.next() // Notify the observer that the operation is complete
      observer.complete() // Complete the Observable
    })
  }

  convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = (error) => reject(error)
      reader.readAsDataURL(file)
    })
  }

  setDatabase(id: any, applicationSettings: object) {
    // Firebase: this.db.updateDatabase('projects/' + id + '/settings/application_settings', applicationSettings)
    this.setSupabaseSettings(Number(id), 'application_settings', applicationSettings as Record<string, any>).subscribe(() => {
      let postData = new URLSearchParams()
      this.token = this.builderService.getToken()
      postData.set('process', 'sync_ui')
      postData.set('projectId', id)
      postData.set('applicationSettings', JSON.stringify(applicationSettings))
      postData.set('token', this.token)

      this.http.post(this.builderUrl + 'update-application-settings', postData.toString(), this.options).subscribe()
    })
  }

  convertBase64ToFile(base64String: string, fileName: string): File {
    // Extract the MIME type safely
    const match = base64String.split(',')[0].match(/:(.*?);/)
    const mimeType = match ? match[1] : 'application/octet-stream' // Fallback to a generic MIME type

    // Decode the Base64 string
    const byteString = atob(base64String.split(',')[1])
    const arrayBuffer = new ArrayBuffer(byteString.length)
    const uint8Array = new Uint8Array(arrayBuffer)

    // Populate the Uint8Array with the byte data
    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i)
    }

    // Create and return a File object
    return new File([uint8Array], fileName, { type: mimeType })
  }

  /**
   * Updates the application settings of a project in the WordPress backend.
   *
   * This function performs the following steps:
   * 1. Prepares the application settings and required metadata, including the project ID and authentication token.
   * 2. Sends the application settings to the WordPress backend for synchronization.
   * 3. Returns the HTTP response from the backend API.
   *
   * @param id The unique project identifier.
   * @param applicationSettings The application settings object to be updated in the WordPress backend.
   * @returns An Observable that emits the response from the WordPress backend.
   */

  setWPApplicationSettings = (id: any, applicationSettings: Object) => {
    let postData = new URLSearchParams()
    this.token = this.builderService.getToken()
    postData.set('process', 'sync_ui')
    postData.set('projectId', id)
    postData.set('applicationSettings', JSON.stringify(applicationSettings))
    postData.set('token', this.token)

    return this.http.post(this.builderUrl + 'update-application-settings', postData.toString(), this.options)
  }

  /**
   * Retrieves the application settings for a specific project from the WordPress backend.
   *
   * This function performs the following steps:
   * 1. Prepares the required data, including the project ID and authentication token.
   * 2. Sends a request to the WordPress backend to fetch the application settings.
   * 3. Returns the HTTP response containing the application settings.
   *
   * @param id The unique project identifier.
   * @returns An Observable that emits the application settings object retrieved from the WordPress backend.
   */

  getApplicationSettings = (id: any) => {
    if (this.builderService.standalone) {
      return new Observable<any>((observer) => {
        // Firebase: this.db.getDatabase('projects/' + id + '/settings/application_settings')
        this.getSupabaseSettings(Number(id), 'application_settings').subscribe(
          (response) => {
            observer.next(response)
            observer.complete()
          },
          (error) => {
            observer.error(error)
          }
        )
      })
    } else {
      let postData = new URLSearchParams()
      this.token = this.builderService.getToken()
      postData.set('process', 'sync_ui')
      postData.set('projectId', id)
      postData.set('token', this.token)

      return this.http.post(this.builderUrl + 'get-application-settings', postData.toString(), this.options)
    }
  }

  getStandAloneApplicationSettings = (id: any) => {
    return new Observable<any>((observer) => {
      // Firebase: this.db.getDatabase('projects/' + id + '/settings/application_settings')
      this.getSupabaseSettings(Number(id), 'application_settings').subscribe(
        (response) => {
          observer.next(response)
          observer.complete()
        },
        (error) => {
          observer.error(error)
        }
      )
    })
  }

  /**
   * Updates the content settings of a project in Firebase and synchronizes them with the WordPress backend.
   *
   * This function performs the following steps:
   * 1. Saves the content settings to the Firebase database under the specified project ID.
   * 2. Prepares and sends the updated content settings to the WordPress backend for synchronization.
   * 3. Emits a response upon successful completion or propagates an error if the process fails.
   *
   * @param id The unique project identifier.
   * @param contentSettings The content settings object to be updated and synchronized.
   * @returns An Observable that emits the response after the settings are successfully updated, or an error if the process fails.
   */

  setContentSettings = (id: any, contentSettings: Object) => {
    contentSettings = JSON.parse(JSON.stringify(contentSettings))
    return new Observable<any>((observer) => {
      // Firebase: this.db.setDatabase('projects/' + id + '/settings/content_settings', contentSettings)
      this.setSupabaseSettings(Number(id), 'content_settings', contentSettings as Record<string, any>).subscribe(
        (response) => {
          let postData = new URLSearchParams()
          this.token = this.builderService.getToken()
          postData.set('process', 'sync_ui')
          postData.set('projectId', id)
          postData.set('contentSettings', JSON.stringify(contentSettings))
          postData.set('token', this.token)

          this.http.post(this.builderUrl + 'update-content-settings', postData.toString(), this.options).subscribe((response_content) => {})
          observer.next(response) // Notify the observer that the operation is complete
          observer.complete() // Complete the Observable
        },
        (error) => {
          observer.error(error) // Notify the observer of an error
        }
      )
    })
  }

  /**
   * Retrieves the content settings for a specific project from the WordPress backend.
   *
   * This function performs the following steps:
   * 1. Prepares the required data, including the project ID and authentication token.
   * 2. Sends a request to the WordPress backend to fetch the content settings.
   * 3. Returns the HTTP response containing the content settings.
   *
   * @param id The unique project identifier.
   * @returns An Observable that emits the content settings object retrieved from the WordPress backend.
   */

  getContentSettings = (id: any) => {
    return new Observable<any>((observer) => {
      // Firebase: this.db.getDatabase('projects/' + id + '/settings/content_settings')
      this.getSupabaseSettings(Number(id), 'content_settings').subscribe(
        (response) => {
          observer.next(response)
          observer.complete()
        },
        (error) => {
          observer.error(error)
        }
      )
    })
  }

  /**
   * Updates the Apple-specific settings of a project in Firebase and synchronizes them with the WordPress backend.
   *
   * This function performs the following steps:
   * 1. Saves the Apple-specific settings to the Firebase database under the specified project ID.
   * 2. Prepares and sends the updated Apple settings to the WordPress backend for synchronization.
   * 3. Emits a response upon successful completion or propagates an error if the process fails.
   *
   * @param id The unique project identifier.
   * @param appleSettings The Apple-specific settings object to be updated and synchronized.
   * @returns An Observable that emits the response after the settings are successfully updated, or an error if the process fails.
   */

  setAppleSettings = (id: any, appleSettings: Object) => {
    appleSettings = JSON.parse(JSON.stringify(appleSettings))
    return new Observable<any>((observer) => {
      // Firebase: this.db.setDatabase('projects/' + id + '/settings/apple_settings', appleSettings)
      this.setSupabaseSettings(Number(id), 'apple_settings', appleSettings as Record<string, any>).subscribe(
        (response) => {
          let postData = new URLSearchParams()
          this.token = this.builderService.getToken()
          postData.set('process', 'sync_ui')
          postData.set('projectId', id)
          postData.set('appleSettings', JSON.stringify(appleSettings))
          postData.set('token', this.token)

          this.http.post(this.builderUrl + 'update-apple-settings', postData.toString(), this.options).subscribe((response_apple) => {})
          observer.next(response) // Notify the observer that the operation is complete
          observer.complete() // Complete the Observable
        },
        (error) => {
          observer.error(error) // Notify the observer of an error
        }
      )
    })
  }

  /**
   * Retrieves the Apple-specific settings for a specific project from the WordPress backend.
   *
   * This function performs the following steps:
   * 1. Prepares the required data, including the project ID and authentication token.
   * 2. Sends a request to the WordPress backend to fetch the Apple-specific settings.
   * 3. Returns the HTTP response containing the Apple-specific settings.
   *
   * @param id The unique project identifier.
   * @returns An Observable that emits the Apple-specific settings object retrieved from the WordPress backend.
   */

  getAppleSettings = (id: any) => {
    let postData = new URLSearchParams()
    this.token = this.builderService.getToken()
    postData.set('process', 'sync_ui')
    postData.set('projectId', id)
    postData.set('token', this.token)

    return this.http.post(this.builderUrl + 'get-apple-settings', postData.toString(), this.options)
    // }
  }

  /**
   * Updates the global settings of a project in Firebase or synchronizes them with the WordPress backend.
   *
   * This function performs the following steps:
   * 1. If the application is in standalone mode:
   *    - Saves the global settings to the Firebase database under the specified project ID.
   *    - Emits a response upon successful completion or propagates an error if the process fails.
   * 2. If not in standalone mode:
   *    - Prepares and sends the global settings to the WordPress backend for synchronization.
   *    - Returns the HTTP response from the backend API.
   *
   * @param id The unique project identifier.
   * @param data The global settings object to be updated and synchronized.
   * @returns An Observable that emits the response after the settings are successfully updated, or an error if the process fails.
   */

  setGlobalSettings = (id: any, data: any) => {
    if (this.builderService.standalone) {
      return new Observable<any>((observer) => {
        // Firebase: this.db.setDatabase('projects/' + id + '/settings/global_settings', data)
        this.setSupabaseSettings(Number(id), 'global_settings', data).subscribe(
          (response) => {
            observer.next(response) // Notify the observer that the operation is complete
            observer.complete() // Complete the Observable
          },
          (error) => {
            observer.error(error) // Notify the observer of an error
          }
        )
      })
    } else {
      let postData = new URLSearchParams()
      this.token = this.builderService.getToken()
      postData.set('process', 'sync_ui')
      postData.set('projectId', id)
      postData.set('token', this.token)
      postData.set('data', JSON.stringify(data))
      return this.http.post(this.builderUrl + 'update-global-settings', postData.toString(), this.options)
    }
  }

  /**
   * Retrieves the global settings of a project from Firebase or the WordPress backend.
   *
   * This function performs the following steps:
   * 1. If the application is in standalone mode:
   *    - Fetches the global settings from the Firebase database for the specified project ID.
   *    - Emits the settings or an empty object if no data is found.
   * 2. If not in standalone mode:
   *    - Sends a request to the WordPress backend to fetch the global settings.
   *    - Returns the HTTP response containing the settings.
   *
   * @param id The unique project identifier.
   * @returns An Observable that emits the global settings object or an empty object if no data is available, or an HTTP response from the WordPress backend.
   */

  getGlobalSettings = (id: any) => {
    if (this.builderService.standalone) {
      return new Observable<any>((observer) => {
        // Firebase: this.db.getDatabase('projects/' + id + '/settings/global_settings')
        this.getSupabaseSettings(Number(id), 'global_settings').subscribe(
          (response) => {
            observer.next(response)
            observer.complete()
          },
          (error) => {
            observer.error(error)
          }
        )
      })
    } else {
      let postData = new URLSearchParams()
      this.token = this.builderService.getToken()
      postData.set('process', 'sync_ui')
      postData.set('projectId', id)
      postData.set('token', this.token)

      return this.http.post(this.builderUrl + 'get-global-settings', postData.toString(), this.options)
    }
  }

  /**
   * Retrieves the menu data for a specific project from Firebase.
   *
   * This function performs the following steps:
   * 1. Fetches the menu data from the Firebase database under the specified project ID.
   * 2. Emits the retrieved menu data if available.
   * 3. Emits an empty object (`{}`) if no data is found.
   *
   * @param id The unique project identifier.
   * @returns An Observable that emits the menu data object or an empty object if no data is available.
   */

  getMenueData(id: any) {
    return new Observable<any>((observer) => {
      this.settingsApi.getSettingsByType(String(id), 'general_settings').then((settings: any) => {
        const response = settings['menus'] ? (typeof settings['menus'] === 'string' ? JSON.parse(settings['menus']) : settings['menus']) : null
        if (response) {
          observer.next(response) // Notify the observer that the operation is complete
          observer.complete() // Complete the Observable
        } else {
          console.log('No data available')
          observer.next({})
          observer.complete() // Complete the Observable
        }
      })
    })
  }

  /**
   * Adds default settings for a new project in Firebase, including application, content, general, Apple, and global settings, as well as default shipping and translations.
   *
   * This function performs the following steps:
   * 1. Defines default settings for various configurations including application, content, and global settings.
   * 2. Fetches default translation data from a JSON file and processes it into a key-value object.
   * 3. Saves the default settings and shipping information to Firebase under the specified project ID.
   * 4. Saves the processed translations to Firebase under the translations path for the project.
   * 5. Emits a response upon successful completion or propagates an error if the process fails.
   *
   * @param id The unique project identifier.
   * @returns An Observable that completes when all default settings and data have been successfully saved or propagates an error if the process fails.
   */

  addProjectDefaultSettings = (id: any,projectType?:string,icon_img?:string) => {
    return new Observable<any>((observer) => {
      let defaults = {
        application_settings: {
          mobile_title_family: 'poppins',
          mobile_title_font_weight: 'bold',
          mobile_title_font_color: '#5e5e5e',
          mobile_base_font_size: '11',
          mobile_text_font_weight: 'regular',
          mobile_text_color: '#707070',
          mobile_primary_color: '#567edc',
          mobile_secondary_color: '#44AF69',
          facebook_hash_id: '',
          facebook_app_id: '',
          facebook_app_name: '',
          android_server_key: '',
          android_sender_id: '',
          google_service_json: '',
          guestModeEnabled: true,
          email_login: true,
          google_login: true,
          phone_login: true,
          twitter_login: true
        },
        content_settings: {
          category_sub_page: '',
          header_background: '',
          contact_email: '',
          mobile_webview_link: '',
          product_title_length: '',
          product_title_dots: ''
        },
        general_settings: {
          application_name: 'Stacks',
          application_description: 'This description is for building process only and it won’t be published to your store accounts.',
          package_name: 'com.stacks.www',
          ipa_sku_number: '1',
          icon: icon_img ? icon_img : 'demo_app_icon.png',
          splash_screen: 'Stacks_splash.png',
          project_type:projectType ? projectType : '',
          // This is for he customer firebase if the data is not added this is used so the app doesn't crash
          firebase_settings: {
            "stacks_user_apiKey": "AIzaSyDsREPTyAol_r8u-IXxT8fOGfGhH82tnBE",
            "stacks_user_authDomain": "dummy-analytics-b68e2.firebaseapp.com",
            "stacks_user_databaseURL": "https://dummy-analytics-b68e2-default-rtdb.firebaseio.com/",
            "stacks_user_projectId": "dummy-analytics-b68e2",
            "stacks_user_storageBucket": "dummy-analytics-b68e2.firebasestorage.app",
            "stacks_user_messagingSenderId": "828061283633",
            "stacks_user_appId": "1:828061283633:web:e20f3874be27d73dc2ac10",
            "stacks_user_measurementId": "G-TVRDMM1PDL",
          }
        },
        apple_settings: {
          email: '',
          password: '',
          phone: '',
          app_specific_pass: '',
          team_name: '',
          team_id: ''
        },
        global_settings: {
          'font-family': 'Poppins',
          'font-size': '14px',
          file: ''
        },
        currency: '$',
        showCountry:true,
        shippingOption: 'show',
        meta_pixel_id: '628192813688005',
        meta_client_token: 'ed23c6bf6ce9251d71e3f383d2068614',
        facebook_displayName: 'Stacks Demo',
        standalone: true
      }
      let default_shipping = [
        {
          countryId: '1761815432215-739',
          countryName: 'United Arab Emirates',
          countryPrice: 70,
          defaultSelection: true,
          isoCode: 'AE'
        },
        {
          countryId: '1761815440765-4541',
          countryName: 'United States',
          countryPrice: 50,
          defaultSelection: false,
          isoCode: 'US'
        },
        {
          countryId: '1771349022085-8626',
          countryName: 'Egypt',
          countryPrice: 100,
          defaultSelection: false,
          isoCode: 'EG'
        }
      ]
      this.saveProjectData(id, defaults, default_shipping, [], [], []).subscribe({
        next: (payload) => observer.next(payload),
        complete: () => observer.complete(),
        error: (error) => observer.error?.(error)
      })
      // let application_settings = {
      //   "mobile_title_family" : "poppins",
      //   "mobile_title_font_weight" : "bold",
      //   "mobile_title_font_color" : "#5e5e5e",
      //   "mobile_base_font_size" : "11",
      //   "mobile_text_font_weight" : "regular",
      //   "mobile_text_color" : "#707070",
      //   "mobile_primary_color" : "#707070",
      //   "mobile_secondary_color" : "#f53c3d",
      //   "facebook_hash_id" : "",
      //   "facebook_app_id" : "",
      //   "facebook_app_name" : "",
      //   "android_server_key" : "",
      //   "android_sender_id" : "",
      //   "google_service_json" : ""
      // };
      // this.setApplicationSettings(id, application_settings).subscribe((resp: any) => {

      // });
      // ❌ Do NOT call observer.next()/complete() here — wait for saveProjectData to finish Seif Was Here.
    })
  }

  /**
   * Creates a project with all its default settings data, products and categories
   * @param id the project id, usually retrieved from the backend after its creation
   * @param defaults the default general, application, content, apple and global settings
   * @param default_shipping the default shipping data
   * @param default_templates the default template to handle fast small vendor creation for multivendor projects
   * @param default_products the default products to handle fast small vendor creation for multivendor projects
   * @param default_categories the default categories to handle fast small vendor creation for multivendor projects
   * @returns 
   */

  private saveProjectData(id: any, defaults: any, default_shipping: any, default_templates: any, default_products: any, default_categories: any) {
    return new Observable<any>((observer) => {
      const serialize = (value: any): string =>
        value !== null && typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')

      // ── Settings → Supabase ─────────────────────────────────────────────────
      const settingsRows: any[] = []

      for (const type of ['application_settings', 'content_settings', 'apple_settings', 'global_settings']) {
        for (const [key, value] of Object.entries(defaults[type] ?? {})) {
          settingsRows.push({ project_id: Number(id), settings_type: type, key, value: serialize(value) })
        }
      }

      // general_settings: write flat keys, but extract firebase_settings into its own type
      const firebaseSettingsData = defaults.general_settings?.firebase_settings
      for (const [key, value] of Object.entries(defaults.general_settings ?? {})) {
        if (key === 'firebase_settings') continue
        settingsRows.push({ project_id: Number(id), settings_type: 'general_settings', key, value: serialize(value) })
      }

      // firebase_settings: each key is a separate row under settings_type='firebase_settings'
      for (const [key, value] of Object.entries(firebaseSettingsData ?? {})) {
        settingsRows.push({ project_id: Number(id), settings_type: 'firebase_settings', key, value: serialize(value) })
      }

      // Flat keys → commerce_settings
      for (const key of ['currency', 'showCountry', 'shippingOption']) {
        if (key in defaults) {
          settingsRows.push({ project_id: Number(id), settings_type: 'commerce_settings', key, value: serialize(defaults[key]) })
        }
      }

      // Flat keys → meta_pixel_settings
      for (const key of ['meta_pixel_id', 'meta_client_token', 'facebook_displayName']) {
        if (key in defaults) {
          settingsRows.push({ project_id: Number(id), settings_type: 'meta_pixel_settings', key, value: serialize(defaults[key]) })
        }
      }

      // All defaults are written through the stacks-agents creation layer:
      // settings rows grouped per settings_type, shipping via the full save
      // (project is brand new, so the replace semantic is equivalent).
      ;(async () => {
        const byType = new Map<string, Record<string, string>>()
        for (const row of settingsRows) {
          if (!byType.has(row.settings_type)) byType.set(row.settings_type, {})
          byType.get(row.settings_type)![row.key] = row.value
        }
        if ('standalone' in defaults) {
          const general = byType.get('general_settings') ?? {}
          general['standalone'] = String(defaults.standalone)
          byType.set('general_settings', general)
        }

        for (const [settingsType, fields] of byType) {
          await this.settingsApi.upsertSettingsByType(String(id), settingsType, fields)
        }

        try {
          await this.shippingApi.saveShipping(String(id), default_shipping as any[])
        } catch (shippingError) {
          console.warn('Default shipping write failed:', shippingError)
        }

        const appName = defaults.general_settings?.application_name
        if (appName) { this.autoGenerateSubdomain(id, appName) }
        observer.next({
          id,
          settings: defaults,
          shipping: default_shipping,
          templates: default_templates,
          products: default_products,
          categories: default_categories
        })
        observer.complete()
      })().catch((error) => observer.error(error))
    })
  }

  /**
   * Generates a valid key for use in Firebase by replacing invalid characters in a string.
   *
   * Firebase keys cannot include certain special characters like `.`, `#`, `$`, `/`, `[`, `]`, and spaces.
   * This function replaces these characters with a space.
   *
   * @param input The string to be transformed into a valid Firebase key.
   * @returns A sanitized string that can be used as a Firebase key.
   */

  generateValidKey(input: string): string {
    return input.replace(/[.#$\/\[\] ]/g, ' ')
  }

  /**
   * Retrieve Google Fonts to be used in the application
   */
  getGoogleFonts = () => {
    return this.http.get('https://www.googleapis.com/webfonts/v1/webfonts?key=AIzaSyA2Z5RS6mlbf7LwObccjvhFYjeRLKj40CE')
  }

  /**
   * Retrieve and save all the settings again
   */
  syncSettings = (id: any) => {
    return new Observable<any>((observer) => {
      // Sync the general settings
      this.getGeneralSettings(id).subscribe((generalSettings: any) => {
        this.setGeneralSettings(id, generalSettings).subscribe(() => {
          // Sync the application settings
          this.getApplicationSettings(id).subscribe((applicationSettings: any) => {
            this.setApplicationSettings(id, applicationSettings).subscribe(() => {
              // Sync the content settings
              this.getContentSettings(id).subscribe((contentSettings: any) => {
                this.setContentSettings(id, contentSettings).subscribe(() => {
                  // Sync the apple settings
                  this.getAppleSettings(id).subscribe((appleSettings: any) => {
                    this.setAppleSettings(id, appleSettings).subscribe(() => {
                      observer.next('All settings Synced') // Notify the observer that the operation is complete
                      observer.complete() // Complete the Observable
                    })
                  })
                })
              })
            })
          })
        })
      })
    })
  }

  /**
   * Auto-generates a subdomain from the application name if one doesn't exist yet.
   * This ensures every project has a reachable website URL.
   *
   * @param projectId The unique project identifier
   * @param appName The application name to generate the subdomain from
   */
  private autoGenerateSubdomain(projectId: string, appName: string): void {
    this.domainService.getDomainSettings(projectId).subscribe(
      (domainSettings) => {
        // Only generate if no subdomain exists yet
        if (!domainSettings || !domainSettings.subdomain) {
          const generatedSubdomain = this.domainService.generateSubdomainFromName(appName)
          this.domainService.saveSubdomain(projectId, generatedSubdomain).subscribe(
            (response) => {
              console.log('Subdomain auto-generated on project creation:', response.subdomain_full)
            },
            (error) => {
              console.error('Failed to auto-generate subdomain on project creation:', error)
            }
          )
        }
      },
      (error) => {
        console.error('Failed to check domain settings on project creation:', error)
      }
    )
  }

/**
 * Retrieves the SEO settings for a specific project from the database.
 * It reads the data from `projects/{id}/settings/seo_settings`
 * and returns an observable with the SEO configuration object.
 */
  getSeoSettings = (id: any) => {
    return new Observable<any>((observer) => {
      // Firebase: this.db.getDatabase('projects/' + id + '/settings/seo_settings')
      this.getSupabaseSettings(Number(id), 'seo_settings').subscribe(
        (response) => {
          observer.next(response)
          observer.complete()
        },
        (error) => {
          observer.error(error)
        }
      )
    })
  }
  
/**
 * Saves or updates the SEO settings for a specific project.
 * The SEO settings object is cloned to avoid reference mutations
 * and then stored at `projects/{id}/settings/seo_settings`.
 */
  setSeoSettings = (id: any, seoSettings: any) => {
    seoSettings = JSON.parse(JSON.stringify(seoSettings))
    return new Observable<any>((observer) => {
      // Firebase: this.db.setDatabase('projects/' + id + '/settings/seo_settings', seoSettings)
      this.setSupabaseSettings(Number(id), 'seo_settings', seoSettings).subscribe(
        (response) => {
          observer.next(response)
          observer.complete()
        },
        (error) => {
          observer.error(error)
        }
      )
    })
  }
}
